import { useEffect, useState, useCallback } from "react";
import { ShieldAlert, LogOut, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * Phase 5Z.19f — Impersonation Safety Bar
 *
 * Sticky top banner shown whenever a super-admin has impersonated a tenant
 * admin. Displays the target tenant, a live countdown of the remaining
 * session time, and a one-click "Zurück zum Super-Admin" button that
 * restores the original super-admin session via /tenants/impersonate/end.
 *
 * State source: `mt_impersonation` localStorage key written by
 * SuperAdminTenants.jsx when impersonation starts.
 */
function readState() {
  try {
    const raw = localStorage.getItem("mt_impersonation");
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj || !obj.expires_at) return null;
    return obj;
  } catch {
    return null;
  }
}

function fmtRemaining(ms) {
  if (ms <= 0) return "00:00";
  const totalSec = Math.floor(ms / 1000);
  const mm = String(Math.floor(totalSec / 60)).padStart(2, "0");
  const ss = String(totalSec % 60).padStart(2, "0");
  return `${mm}:${ss}`;
}

export default function ImpersonationBar() {
  const [state, setState] = useState(() => readState());
  const [now, setNow] = useState(Date.now());
  const [busy, setBusy] = useState(false);

  // Cross-tab sync: re-read on storage events
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "mt_impersonation" || e.key === null) {
        setState(readState());
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Tick countdown each second while active
  useEffect(() => {
    if (!state) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [state]);

  const endImpersonation = useCallback(async () => {
    if (busy) return;
    if (!window.confirm("Impersonation beenden und zur Super-Admin-Sitzung zurückkehren?")) return;
    setBusy(true);
    try {
      // Try server-side restoration first (audited, validates origin user still exists)
      let restored = false;
      try {
        const { data } = await api.post("/tenants/impersonate/end");
        if (data?.access_token) {
          localStorage.setItem("mt_token", data.access_token);
          restored = true;
        }
      } catch (e) {
        // Fallback: use locally saved super-admin token if available
        const saved = localStorage.getItem("mt_token_super_origin");
        if (saved) {
          localStorage.setItem("mt_token", saved);
          restored = true;
        } else {
          throw e;
        }
      }
      if (restored) {
        localStorage.removeItem("mt_impersonation");
        localStorage.removeItem("mt_token_super_origin");
        toast.success("Zurück zum Super-Admin");
        setTimeout(() => { window.location.href = "/super-admin/tenants"; }, 400);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Beenden fehlgeschlagen — bitte erneut anmelden");
      setBusy(false);
    }
  }, [busy]);

  if (!state) return null;

  const remaining = state.expires_at - now;
  const expired = remaining <= 0;
  const warning = remaining > 0 && remaining < 5 * 60 * 1000; // < 5 min

  return (
    <div
      data-testid="impersonation-safety-bar"
      className={`sticky top-0 left-0 right-0 z-[60] w-full text-white shadow-lg ${
        expired ? "bg-red-700" : warning ? "bg-amber-600" : "bg-rose-700"
      }`}
      role="alert"
    >
      <div className="max-w-screen-2xl mx-auto px-4 py-2 flex items-center gap-3 text-sm">
        <ShieldAlert className="w-5 h-5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-semibold truncate" data-testid="impersonation-bar-title">
            Impersonation aktiv — Sie handeln als{" "}
            <span className="underline">{state.target_user_email || "—"}</span>
            {state.target_tenant_name ? ` (Mandant: ${state.target_tenant_name})` : ""}
          </div>
          <div className="text-xs opacity-90 truncate">
            Alle Aktionen werden im Audit-Log Ihrer Super-Admin-Identität
            {state.origin_email ? ` (${state.origin_email})` : ""} protokolliert.
          </div>
        </div>
        <div
          className={`flex items-center gap-1.5 font-mono text-sm px-2 py-1 rounded ${
            expired ? "bg-black/40" : "bg-black/25"
          }`}
          data-testid="impersonation-bar-countdown"
          title="Verbleibende Sitzungszeit"
        >
          <Clock className="w-4 h-4" />
          {expired ? "ABGELAUFEN" : fmtRemaining(remaining)}
        </div>
        <button
          type="button"
          onClick={endImpersonation}
          disabled={busy}
          data-testid="impersonation-end-btn"
          className="inline-flex items-center gap-1.5 bg-white text-rose-800 hover:bg-rose-50 disabled:opacity-60 disabled:cursor-not-allowed font-semibold px-3 py-1.5 rounded shadow-sm transition-colors"
        >
          <LogOut className="w-4 h-4" />
          {busy ? "Beenden…" : "Zurück zum Super-Admin"}
        </button>
      </div>
    </div>
  );
}
